import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import PartnerDetailModal from './PartnerDetailModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PARTNER_TYPES = [
  { value: 'cafe', label: 'Kafe', icon: '☕' },
  { value: 'restaurant', label: 'Restoran', icon: '🍽️' },
  { value: 'university', label: 'Üniversite', icon: '🎓' },
  { value: 'event', label: 'Etkinlik', icon: '🎉' },
  { value: 'coworking', label: 'Coworking', icon: '💼' },
  { value: 'influencer', label: 'Influencer', icon: '⭐' },
  { value: 'hotel', label: 'Otel', icon: '🏨' },
  { value: 'store', label: 'Mağaza', icon: '🛍️' },
  { value: 'other', label: 'Diğer', icon: '📍' },
];

const PartnersPage = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null); // For detail modal
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    partner_type: 'cafe',
    notes: '',
    contact_email: '',
    contact_phone: ''
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/api/growth-hub/partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartners(data.partners || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch partners:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingPartner) {
        await axios.put(
          `${API_URL}/api/growth-hub/partners/${editingPartner.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_URL}/api/growth-hub/partners`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      setShowModal(false);
      setEditingPartner(null);
      setFormData({ name: '', slug: '', partner_type: 'cafe', notes: '', contact_email: '', contact_phone: '' });
      fetchPartners();
    } catch (error) {
      console.error('Failed to save partner:', error);
      alert('Hata: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      slug: partner.slug,
      partner_type: partner.partner_type,
      notes: partner.notes || '',
      contact_email: partner.contact_email || '',
      contact_phone: partner.contact_phone || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (partnerId) => {
    if (!window.confirm('Bu partneri silmek istediğinize emin misiniz?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/growth-hub/partners/${partnerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPartners();
    } catch (error) {
      console.error('Failed to delete partner:', error);
      alert('Silme hatası: ' + error.message);
    }
  };

  const getTypeInfo = (type) => PARTNER_TYPES.find(t => t.value === type) || PARTNER_TYPES[8];
  
  const copyLandingUrl = (slug) => {
    const url = `${window.location.origin}/gift/${slug}`;
    navigator.clipboard.writeText(url);
    alert('URL kopyalandı!');
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.6)' }}>Yükleniyor...</div>;
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px' }}>🏪 Partnerler ({partners.length})</h2>
          <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
            Kafeler, üniversiteler, etkinlikler ve daha fazlası
          </p>
        </div>
        <button
          onClick={() => {
            setEditingPartner(null);
            setFormData({ name: '', slug: '', partner_type: 'cafe', notes: '', contact_email: '', contact_phone: '' });
            setShowModal(true);
          }}
          style={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <span>+</span>
          <span>Yeni Partner</span>
        </button>
      </div>

      {/* Partners Grid */}
      {partners.length === 0 ? (
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '20px',
          padding: '80px 40px',
          textAlign: 'center'
        }}>
          <div style={{ 
            width: '80px',
            height: '80px',
            background: 'rgba(139, 92, 246, 0.1)',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            fontSize: '40px'
          }}>🏪</div>
          <h3 style={{ margin: '0 0 12px', color: 'white', fontSize: '20px', fontWeight: '600' }}>Henüz partner yok</h3>
          <p style={{ margin: '0 0 24px', color: 'rgba(255,255,255,0.5)', fontSize: '15px', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
            İlk partnerinizi ekleyerek Büyüme Merkezini başlatın
          </p>
          <button
            onClick={() => setShowModal(true)}
            style={{
              padding: '14px 28px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              border: 'none',
              borderRadius: '12px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '600'
            }}
          >
            + İlk Partneri Ekle
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 420px), 1fr))', gap: '24px' }}>
          {partners.map((partner) => {
            const typeInfo = getTypeInfo(partner.partner_type);
            const conversionRate = partner.signup_count && partner.total_scans 
              ? ((partner.signup_count / partner.total_scans) * 100).toFixed(1) 
              : 0;
            return (
              <motion.div
                key={partner.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                style={{
                  background: 'linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '20px',
                  padding: 'clamp(16px, 4vw, 28px)',
                  position: 'relative',
                  transition: 'border-color 0.3s, box-shadow 0.3s, transform 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.4)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(139, 92, 246, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Status Badge */}
                <div style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: partner.status === 'active' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                  color: partner.status === 'active' ? '#22c55e' : '#ef4444',
                  padding: '8px 14px',
                  borderRadius: '24px',
                  fontSize: '11px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  border: `1px solid ${partner.status === 'active' ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`
                }}>
                  <span style={{ 
                    width: '6px', 
                    height: '6px', 
                    borderRadius: '50%', 
                    background: partner.status === 'active' ? '#22c55e' : '#ef4444',
                    boxShadow: partner.status === 'active' ? '0 0 8px #22c55e' : '0 0 8px #ef4444'
                  }}></span>
                  {partner.status === 'active' ? 'Aktif' : 'Pasif'}
                </div>

                {/* Partner Header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '18px', marginBottom: '24px' }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    background: 'linear-gradient(135deg, rgba(139,92,246,0.25), rgba(99,102,241,0.2))',
                    borderRadius: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '32px',
                    flexShrink: 0,
                    border: '1px solid rgba(139,92,246,0.2)'
                  }}>
                    {typeInfo.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ margin: 0, fontSize: '20px', color: 'white', fontWeight: '700', letterSpacing: '-0.3px' }}>{partner.name}</h3>
                    <p style={{ margin: '8px 0 0', fontSize: '13px', color: 'rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ background: 'rgba(139,92,246,0.15)', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', color: '#c084fc' }}>{typeInfo.label}</span>
                      <span style={{ color: 'rgba(255,255,255,0.45)' }}>•</span>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>/{partner.slug}</span>
                    </p>
                  </div>
                </div>

                {/* Premium Stats Grid - Responsive: 4 cols desktop, 2 cols mobile */}
                <div style={{
                  background: 'rgba(0,0,0,0.25)',
                  borderRadius: '14px',
                  padding: 'clamp(12px, 3vw, 20px)',
                  marginBottom: '16px',
                  border: '1px solid rgba(255,255,255,0.04)'
                }}>
                  {/* Row 1 */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(70px, 1fr))',
                    gap: 'clamp(8px, 2vw, 16px)',
                    marginBottom: 'clamp(10px, 2vw, 16px)',
                    paddingBottom: 'clamp(10px, 2vw, 16px)',
                    borderBottom: '1px solid rgba(255,255,255,0.06)'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '500' }}>Şube</div>
                      <div style={{ fontSize: 'clamp(18px, 4vw, 24px)', color: 'white', fontWeight: '700' }}>{partner.branch_count || 0}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '500' }}>QR</div>
                      <div style={{ fontSize: 'clamp(18px, 4vw, 24px)', color: '#8b5cf6', fontWeight: '700' }}>{partner.qr_count || 0}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '500' }}>Kampanya</div>
                      <div style={{ fontSize: 'clamp(18px, 4vw, 24px)', color: '#06b6d4', fontWeight: '700' }}>{partner.campaign_count || 0}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '500' }}>Kayıt</div>
                      <div style={{ fontSize: 'clamp(18px, 4vw, 24px)', color: '#22c55e', fontWeight: '700' }}>{partner.signup_count || 0}</div>
                    </div>
                  </div>
                  {/* Row 2 */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(70px, 1fr))',
                    gap: 'clamp(8px, 2vw, 16px)',
                    marginBottom: 'clamp(10px, 2vw, 16px)',
                    paddingBottom: 'clamp(10px, 2vw, 16px)',
                    borderBottom: '1px solid rgba(255,255,255,0.06)'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '500' }}>Tarama</div>
                      <div style={{ fontSize: 'clamp(16px, 3.5vw, 20px)', color: '#a78bfa', fontWeight: '600' }}>{partner.total_scans || 0}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '500' }}>Ziyaret</div>
                      <div style={{ fontSize: 'clamp(16px, 3.5vw, 20px)', color: '#3b82f6', fontWeight: '600' }}>{partner.total_views || 0}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '500' }}>Dönüşüm</div>
                      <div style={{ fontSize: 'clamp(16px, 3.5vw, 20px)', color: '#f472b6', fontWeight: '600' }}>{conversionRate}%</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '500' }}>Gelir</div>
                      <div style={{ fontSize: 'clamp(16px, 3.5vw, 20px)', color: '#fbbf24', fontWeight: '600' }}>{partner.revenue || partner.total_revenue || 0}₺</div>
                    </div>
                  </div>
                  {/* Row 3 - Gift & Redeem */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(70px, 1fr))',
                    gap: 'clamp(8px, 2vw, 16px)'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '500' }}>Gift</div>
                      <div style={{ fontSize: 'clamp(14px, 3vw, 18px)', color: '#ec4899', fontWeight: '600' }}>{partner.total_gifts || 0}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '500' }}>Redeem</div>
                      <div style={{ fontSize: 'clamp(14px, 3vw, 18px)', color: '#14b8a6', fontWeight: '600' }}>{partner.total_redeems || 0}</div>
                    </div>
                    <div style={{ textAlign: 'center', gridColumn: 'span 2' }}>
                      <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '500' }}>Landing URL</div>
                      <div style={{ fontSize: '11px', color: '#22d3ee', fontWeight: '500', fontFamily: 'monospace' }}>/gift/{partner.slug}</div>
                    </div>
                  </div>
                </div>

                {/* Premium Actions - Responsive */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setSelectedPartner(partner)}
                    style={{
                      flex: '1 1 100%',
                      background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(16, 185, 129, 0.1))',
                      border: '1px solid rgba(34, 197, 94, 0.25)',
                      color: '#22c55e',
                      padding: 'clamp(10px, 2.5vw, 14px) 12px',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: 'clamp(12px, 2.5vw, 14px)',
                      fontWeight: '600',
                      marginBottom: '4px',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(34, 197, 94, 0.25), rgba(16, 185, 129, 0.2))';
                      e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(16, 185, 129, 0.1))';
                      e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.25)';
                    }}
                  >
                    Şubeler & QR
                  </button>
                  <button
                    onClick={() => copyLandingUrl(partner.slug)}
                    style={{
                      flex: '1 1 auto',
                      minWidth: '80px',
                      background: 'rgba(139,92,246,0.12)',
                      border: '1px solid rgba(139,92,246,0.25)',
                      color: '#c084fc',
                      padding: 'clamp(8px, 2vw, 12px) 10px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(139,92,246,0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(139,92,246,0.12)';
                    }}
                  >
                    URL
                  </button>
                  <button
                    onClick={() => window.open(`/gift/${partner.slug}`, '_blank')}
                    style={{
                      background: 'rgba(6,182,212,0.12)',
                      border: '1px solid rgba(6,182,212,0.25)',
                      color: '#22d3ee',
                      padding: 'clamp(8px, 2vw, 12px) 10px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(6,182,212,0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(6,182,212,0.12)';
                    }}
                  >
                    Landing
                  </button>
                  <button
                    onClick={() => handleEdit(partner)}
                    style={{
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      color: 'rgba(255,255,255,0.8)',
                      padding: 'clamp(8px, 2vw, 12px) 10px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                    }}
                  >
                    Düzenle
                  </button>
                  <button
                    onClick={() => handleDelete(partner.id)}
                    style={{
                      background: 'rgba(239,68,68,0.1)',
                      border: '1px solid rgba(239,68,68,0.2)',
                      color: '#f87171',
                      padding: 'clamp(8px, 2vw, 12px) 10px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(239,68,68,0.18)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(239,68,68,0.1)';
                    }}
                  >
                    Sil
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.95)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '20px'
            }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
                border: '1px solid rgba(139,92,246,0.3)',
                borderRadius: '20px',
                padding: '32px',
                width: '100%',
                maxWidth: '500px',
                maxHeight: '90vh',
                overflowY: 'auto'
              }}
            >
              <h2 style={{ margin: '0 0 24px', fontSize: '20px', color: 'white' }}>
                {editingPartner ? '✏️ Partner Düzenle' : '➕ Yeni Partner'}
              </h2>

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
                    Partner Adı *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Örn: Cafe Latte"
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px',
                      color: 'white',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
                    Slug (URL) - boş bırakırsan otomatik oluşturulur
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>/gift/</span>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                      placeholder="cafelatte"
                      style={{
                        flex: 1,
                        padding: '12px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '10px',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
                    Partner Tipi
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                    {PARTNER_TYPES.map(type => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, partner_type: type.value })}
                        style={{
                          padding: '10px',
                          background: formData.partner_type === type.value 
                            ? 'linear-gradient(135deg, rgba(139,92,246,0.4), rgba(99,102,241,0.4))'
                            : 'rgba(255,255,255,0.05)',
                          border: formData.partner_type === type.value 
                            ? '2px solid #8b5cf6'
                            : '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '10px',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '12px',
                          textAlign: 'center'
                        }}
                      >
                        <div style={{ fontSize: '20px', marginBottom: '4px' }}>{type.icon}</div>
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
                    İletişim Email
                  </label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="iletisim@cafe.com"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px',
                      color: 'white',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
                    Notlar
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Partner hakkında notlar..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px',
                      color: 'white',
                      fontSize: '14px',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                      border: 'none',
                      borderRadius: '10px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    {editingPartner ? 'Güncelle' : 'Oluştur'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Partner Detail Modal (Branch & QR Management) */}
      {selectedPartner && (
        <PartnerDetailModal
          partner={selectedPartner}
          onClose={() => setSelectedPartner(null)}
          onUpdate={fetchPartners}
        />
      )}
    </div>
  );
};

export default PartnersPage;
